class SalutContactSelect extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-select-input]');
    this.trigger = this.querySelector('.salut-contact__select-trigger');
    this.value = this.querySelector('[data-select-value]');
    this.panel = this.querySelector('.salut-contact__select-panel');
    this.options = Array.from(this.querySelectorAll('.salut-contact__select-option'));
    this.activeIndex = -1;
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  connectedCallback() {
    if (!this.trigger || !this.panel || !this.input) return;
    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
    this.panel.addEventListener('keydown', (event) => this.onPanelKeydown(event));
    this.options.forEach((option, index) => {
      option.addEventListener('click', () => this.select(index));
      option.addEventListener('mousemove', () => this.setActive(index));
    });
  }

  get isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    const selected = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.setActive(selected > -1 ? selected : 0);
    this.panel.focus();
    document.addEventListener('click', this.onDocumentClick);
  }

  close(focusTrigger) {
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
    document.removeEventListener('click', this.onDocumentClick);
    if (focusTrigger) this.trigger.focus();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  setActive(index) {
    this.activeIndex = index;
    this.options.forEach((option, i) => option.classList.toggle('is-active', i === index));
    if (index > -1) this.options[index].scrollIntoView({ block: 'nearest' });
  }

  select(index) {
    const option = this.options[index];
    if (!option) return;
    this.options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
    this.input.value = option.dataset.value;
    this.value.textContent = option.dataset.value;
    this.value.removeAttribute('data-placeholder');
    this.close(true);
    this.dispatchEvent(new CustomEvent('salut-contact:change', { bubbles: true }));
  }

  move(step) {
    if (!this.options.length) return;
    const next = (this.activeIndex + step + this.options.length) % this.options.length;
    this.setActive(next);
  }

  onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
    }
  }

  onPanelKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.select(this.activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        this.close(true);
        break;
      case 'Tab':
        this.close();
        break;
    }
  }
}

class SalutContactForm extends HTMLElement {
  constructor() {
    super();
    this.controls = Array.from(this.querySelectorAll('[data-required]'));
    this.submit = this.querySelector('[data-submit]');
    this.textarea = this.querySelector('.salut-contact__input--area');
    this.form = this.closest('form');
    this.submitted = false;
  }

  connectedCallback() {
    this.controls.forEach((control) => {
      const error = this.errorFor(control);
      if (error) control.dataset.errorDefault = error.textContent.trim();
      control.addEventListener('blur', () => this.showError(control));
    });

    this.addEventListener('input', () => this.refresh());
    this.addEventListener('salut-contact:change', () => {
      this.refresh();
      this.showError(this.controls.find((control) => control.tagName === 'BUTTON'));
    });

    if (this.textarea) {
      this.textarea.addEventListener('input', () => this.grow());
      this.grow();
    }

    if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

    this.refresh();
  }

  errorFor(control) {
    const field = control.closest('.salut-contact__field');
    return field ? field.querySelector('[data-error]') : null;
  }

  valueOf(control) {
    if (control.tagName === 'BUTTON') {
      const input = control.closest('salut-contact-select').querySelector('[data-select-input]');
      return input ? input.value.trim() : '';
    }
    return control.value.trim();
  }

  problem(control) {
    const value = this.valueOf(control);
    if (!value) return 'empty';
    if (control.dataset.email !== undefined && !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value)) return 'invalid';
    return '';
  }

  showError(control) {
    if (!control) return;
    const error = this.errorFor(control);
    if (!error) return;
    const problem = this.problem(control);
    if (!problem) {
      error.hidden = true;
      return;
    }
    if (problem === 'invalid' && control.dataset.errorInvalid) {
      error.textContent = control.dataset.errorInvalid;
    } else if (problem === 'empty' && control.dataset.errorEmpty) {
      error.textContent = control.dataset.errorEmpty;
    } else {
      error.textContent = control.dataset.errorDefault;
    }
    error.hidden = false;
  }

  refresh() {
    const valid = this.controls.every((control) => !this.problem(control));
    if (this.submit) this.submit.disabled = !valid;
    if (this.submitted) this.controls.forEach((control) => this.showError(control));
  }

  grow() {
    this.textarea.style.height = 'auto';
    this.textarea.style.height = `${this.textarea.scrollHeight}px`;
  }

  onSubmit(event) {
    this.submitted = true;
    const invalid = this.controls.filter((control) => this.problem(control));
    invalid.forEach((control) => this.showError(control));
    if (!invalid.length) return;
    event.preventDefault();
    invalid[0].focus();
  }
}

customElements.define('salut-contact-select', SalutContactSelect);
customElements.define('salut-contact-form', SalutContactForm);
